import { useCallback, useEffect, useMemo, useRef, useState } from "react";



// Normalize any date-like value to a stable day key used by queue groups.
function getQueueDayKey(dateValue) {

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;

}



const QueueDaySlider = ({
  queueDayGroups,
  selectedQueueDayKey,
  onSelectedQueueDayKeyChange,
  isPopupOpen,
}) => {

  // Track element refs used for pointer math and pointer capture ownership.
  const queueDaySliderTrackRef = useRef(null);

  const queueDaySliderPointerIdRef = useRef(null);

  // True while the user is actively dragging the slider thumb/track.
  const [isQueueDaySliderDragging, setIsQueueDaySliderDragging] = useState(false);



  // Resolve the selected day key to an index, defaulting to 0 when missing.
  const selectedQueueDayIndex = useMemo(() => {

    if (queueDayGroups.length === 0) return 0;

    const matchIndex = queueDayGroups.findIndex((group) => group.dayKey === selectedQueueDayKey);

    return matchIndex >= 0 ? matchIndex : 0;

  }, [queueDayGroups, selectedQueueDayKey]);



  // Derived values used for slider rendering and accessibility attributes.
  const selectedQueueDayGroup = queueDayGroups[selectedQueueDayIndex] || null;



  const queueDaySliderMaxIndex = Math.max(queueDayGroups.length - 1, 0);



  const queueDaySliderPercent = queueDaySliderMaxIndex > 0
    ? (selectedQueueDayIndex / queueDaySliderMaxIndex) * 100
    : 0;



  // Left label shows "Today" when newest group is today, otherwise "Newest".
  const queueDaySliderLeftLabel = useMemo(() => {

    if (queueDayGroups.length === 0) return "Today";

    const todayDayKey = getQueueDayKey(new Date());

    return queueDayGroups[0]?.dayKey === todayDayKey ? "Today" : "Newest";

  }, [queueDayGroups]);



  // Clamp and commit an index back to the parent via selected day key.
  const setQueueDayByIndex = useCallback((nextIndex) => {

    if (queueDayGroups.length === 0) return;

    const clampedIndex = Math.max(0, Math.min(queueDaySliderMaxIndex, nextIndex));

    const selectedDay = queueDayGroups[clampedIndex];

    if (selectedDay && selectedDay.dayKey !== selectedQueueDayKey) {

      onSelectedQueueDayKeyChange(selectedDay.dayKey);

    }
  }, [queueDayGroups, queueDaySliderMaxIndex, selectedQueueDayKey, onSelectedQueueDayKeyChange]);



  // Translate pointer X coordinates into a queue day index.
  const setQueueDayByClientX = useCallback((clientX) => {

    const trackElement = queueDaySliderTrackRef.current;

    if (!trackElement || queueDayGroups.length === 0) return;

    const rect = trackElement.getBoundingClientRect();

    if (rect.width <= 0) return;

    const normalized = (clientX - rect.left) / rect.width;

    const clamped = Math.max(0, Math.min(1, normalized));

    const nextIndex = Math.round(clamped * queueDaySliderMaxIndex);

    setQueueDayByIndex(nextIndex);

  }, [queueDayGroups.length, queueDaySliderMaxIndex, setQueueDayByIndex]);



  // Start drag interaction, select current position immediately, and capture pointer.
  const handleQueueDaySliderPointerDown = useCallback((event) => {

    if (queueDayGroups.length === 0) return;

    event.preventDefault();

    setIsQueueDaySliderDragging(true);

    setQueueDayByClientX(event.clientX);

    if (event.currentTarget?.setPointerCapture) {

      event.currentTarget.setPointerCapture(event.pointerId);

      queueDaySliderPointerIdRef.current = event.pointerId;

    }
  }, [queueDayGroups.length, setQueueDayByClientX]);



  // Continue updating selection while dragging with the captured pointer.
  const handleQueueDaySliderPointerMove = useCallback((event) => {

    if (!isQueueDaySliderDragging) return;

    if (
      queueDaySliderPointerIdRef.current !== null &&
      event.pointerId !== queueDaySliderPointerIdRef.current
    ) {

      return;

    }

    setQueueDayByClientX(event.clientX);

  }, [isQueueDaySliderDragging, setQueueDayByClientX]);



  // End drag interaction and release pointer capture ownership.
  const handleQueueDaySliderPointerUp = useCallback((event) => {

    if (
      queueDaySliderPointerIdRef.current !== null &&
      event.pointerId !== queueDaySliderPointerIdRef.current
    ) {

      return;

    }

    if (
      event.currentTarget?.hasPointerCapture &&
      event.currentTarget.hasPointerCapture(event.pointerId)
    ) {

      event.currentTarget.releasePointerCapture(event.pointerId);

    }

    queueDaySliderPointerIdRef.current = null;

    setIsQueueDaySliderDragging(false);

  }, []);



  // Keyboard support when slider track has focus.
  const handleQueueDaySliderKeyDown = useCallback((event) => {

    if (queueDayGroups.length === 0) return;

    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {

      event.preventDefault();

      setQueueDayByIndex(selectedQueueDayIndex - 1);

      return;

    }

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {

      event.preventDefault();

      setQueueDayByIndex(selectedQueueDayIndex + 1);

      return;

    }

    if (event.key === "Home") {

      event.preventDefault();

      setQueueDayByIndex(0);

      return;

    }

    if (event.key === "End") {

      event.preventDefault();

      setQueueDayByIndex(queueDaySliderMaxIndex);

    }
  }, [queueDayGroups.length, queueDaySliderMaxIndex, selectedQueueDayIndex, setQueueDayByIndex]);



  // Global left/right keyboard support while popup is closed and slider is visible.
  useEffect(() => {

    if (isPopupOpen || queueDayGroups.length === 0) return undefined;

    const handleQueueDayArrowKeyDown = (event) => {

      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

      if (
        queueDaySliderTrackRef.current &&
        queueDaySliderTrackRef.current.contains(event.target)
      ) {

        return;

      }

      event.preventDefault();

      if (event.key === "ArrowLeft") {

        setQueueDayByIndex(selectedQueueDayIndex - 1);

        return;

      }

      setQueueDayByIndex(selectedQueueDayIndex + 1);

    };

    window.addEventListener("keydown", handleQueueDayArrowKeyDown);

    return () => window.removeEventListener("keydown", handleQueueDayArrowKeyDown);

  }, [isPopupOpen, queueDayGroups.length, selectedQueueDayIndex, setQueueDayByIndex]);



  // Hide the slider entirely when there are no grouped queue days.
  if (queueDayGroups.length === 0) return null;



  return (

    <div className="ai-queue-day-slider">

      <div className="ai-queue-day-slider__rail-wrap">

        {/* Interactive slider track used for pointer + keyboard day selection. */}
        <div
          className="ai-queue-day-slider__track"
          ref={queueDaySliderTrackRef}
          onPointerDown={handleQueueDaySliderPointerDown}
          onPointerMove={handleQueueDaySliderPointerMove}
          onPointerUp={handleQueueDaySliderPointerUp}
          onPointerCancel={handleQueueDaySliderPointerUp}
          onKeyDown={handleQueueDaySliderKeyDown}
          tabIndex={0}
          role="slider"
          aria-label="Queue day slider"
          aria-valuemin={0}
          aria-valuemax={queueDaySliderMaxIndex}
          aria-valuenow={selectedQueueDayIndex}
          aria-valuetext={selectedQueueDayGroup?.label || "No date"}
        >

          {/* Base rail + selected range fill to visualize selected day position. */}
          <div className="ai-queue-day-slider__line" aria-hidden="true" />

          <div
            className="ai-queue-day-slider__line-fill"
            style={{ width: `${queueDaySliderPercent}%` }}
            aria-hidden="true"
          />

          {/* Fixed timeline points for each available queue day group. */}
          <div className="ai-queue-day-slider__points" aria-hidden="true">
            {queueDayGroups.map((group, index) => {

              // Position points on the same 0-100 coordinate system as the thumb.
              // This keeps the active point and thumb perfectly aligned.
              const pointPercent = queueDaySliderMaxIndex > 0
                ? (index / queueDaySliderMaxIndex) * 100
                : 0;

              return (

                <span
                  key={`queue-day-point-${group.dayKey}`}
                  className={`ai-queue-day-slider__point ${index < selectedQueueDayIndex
                    ? "is-before"
                    : index === selectedQueueDayIndex
                      ? "is-active"
                      : ""
                    }`}
                  style={{ left: `${pointPercent}%` }}
                />

              );

            })}

          </div>

          {isQueueDaySliderDragging && (

            /* Drag-only tooltip to show exact selected day label while scrubbing. */
            <div
              className="ai-queue-day-slider__tooltip"
              style={{ left: `${queueDaySliderPercent}%` }}
            >

              {selectedQueueDayGroup?.label || "No date"}

            </div>

          )}

          {/* Thumb follows the selected day index. */}
          <div
            className="ai-queue-day-slider__thumb"
            style={{ left: `${queueDaySliderPercent}%` }}
            aria-hidden="true"
          />

        </div>

      </div>

      {/* Left/newest, current, and right/oldest text labels for orientation. */}
      <div className="ai-queue-day-slider__labels">

        <span>{queueDaySliderLeftLabel}</span>

        <span>{selectedQueueDayGroup?.label || "No date"}</span>

        <span>Oldest</span>

      </div>

    </div>
    
  );

};

export default QueueDaySlider;



